/**
 * =============================================================================
 * IDS CRM Web - Tasks (Scheduling) Page
 * =============================================================================
 *
 * PURPOSE:
 * Scheduling view with week/month calendar and task cards.
 * Full 7-day week and 24-hour support for 24/7 operations.
 *
 * =============================================================================
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getCapabilities } from '../utils/permissions';
import { useToast } from '../components/Toast';
import CustomerSearchSelect from '../components/CustomerSearchSelect';
import { useListMemory, useRememberList } from '../hooks/useListMemory';
import {
  toDateKey,
  dateKeyOf,
  fromDateKey,
  startOfDay,
  addDays,
  addMonths,
  startOfWeek,
  startOfMonth,
  monthGridDays,
  daysBetween,
  formatDateKey
} from '../utils/dates';
import './Tasks.css';

const DEFAULT_CALENDAR_FILTERS = {
  crm: true,
  reactive_tickets: true,
  scheduled_work: true,
  scheduled_maintenance: true,
  status: '',
  assigned_user: '',
  assigned_group: '',
  my: false,
  customer_id: '',
  customer_label: '',
  distributor_id: '',
  distributor_label: '',
  asset_type: '',
  priority: ''
};

// Saved filters that predate the typeahead pickers carry ids without display
// labels - an active filter would look like an empty box. Drop those ids.
function sanitizeSavedFilters(parsed) {
  if (parsed.customer_id && !parsed.customer_label) parsed.customer_id = '';
  if (parsed.distributor_id && !parsed.distributor_label) parsed.distributor_id = '';
  return parsed;
}

function Tasks() {
  const { authFetch, user } = useAuth();
  const { toastElement, showToast } = useToast();
  const calendarRef = useRef(null);
  const filterStorageReady = useRef(false);
  // Coming back from a job (a "←" link or the browser's Back) shows the
  // schedule as it was left: the same view, period and filters. A fresh visit
  // opens this week.
  const memory = useListMemory('/tasks');
  const { returning } = memory;

  const [tasks, setTasks] = useState([]);
  const [onCallDays, setOnCallDays] = useState({});
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState(() => memory.initial('view', 'week')); // day, week, month, list
  // One date drives every view, so Week -> Month shows the month of the week
  // on screen and Today resets them all. Always local midnight: the old
  // per-view dates kept the time the page was opened, which moved the day.
  // Remembered as its calendar day ('YYYY-MM-DD'), never as an instant.
  const [anchorDate, setAnchorDate] = useState(() => fromDateKey(memory.initial('date', '')) || startOfDay(new Date()));
  const selectedDate = anchorDate;
  const weekStart = startOfWeek(anchorDate);
  const monthStart = startOfMonth(anchorDate);
  const [selectedTask, setSelectedTask] = useState(null);
  const [engineers, setEngineers] = useState([]);
  const [assetTypes, setAssetTypes] = useState([]);
  // Bumped on Reset so the search pickers remount with empty boxes.
  const [filterResetKey, setFilterResetKey] = useState(0);
  const [filters, setFilters] = useState(() => {
    // Coming back, the filters are the ones this tab was left on.
    const remembered = memory.initial('filters', null);
    if (remembered) return { ...DEFAULT_CALENDAR_FILTERS, ...remembered };
    try {
      const saved = localStorage.getItem('ids-crm-calendar-filters');
      return saved ? { ...DEFAULT_CALENDAR_FILTERS, ...sanitizeSavedFilters(JSON.parse(saved)) } : DEFAULT_CALENDAR_FILTERS;
    } catch {
      return DEFAULT_CALENDAR_FILTERS;
    }
  });
  const [savingMaintenance, setSavingMaintenance] = useState(false);
  const [maintenanceForm, setMaintenanceForm] = useState({
    current_due_date: '',
    scheduled_start_time: '',
    scheduled_end_time: '',
    notes: '',
    assigned_engineer_ids: []
  });

  function formatWeekRange(start) {
    const end = new Date(start);
    end.setDate(end.getDate() + 6);
    const startDay = start.getDate();
    const endDay = end.getDate();
    const startMonth = start.toLocaleString('default', { month: 'short' });
    const endMonth = end.toLocaleString('default', { month: 'short' });
    const year = start.getFullYear();
    if (startMonth === endMonth) {
      return `${startDay} – ${endDay} ${startMonth} ${year}`;
    }
    return `${startDay} ${startMonth} – ${endDay} ${endMonth} ${year}`;
  }

  function formatMonthRange(start) {
    return start.toLocaleString('default', { month: 'long', year: 'numeric' });
  }

  // Fetch exactly the days on screen. The month fetch used to stop at the
  // Sunday after month end (the grid's last row stayed blank) and, for a
  // month starting on a Sunday, began on the 2nd.
  const getDateRange = useCallback(() => {
    if (viewMode === 'day') {
      return { start: anchorDate, end: anchorDate };
    }
    if (viewMode === 'month') {
      const days = monthGridDays(anchorDate);
      return { start: days[0], end: days[days.length - 1] };
    }
    const start = startOfWeek(anchorDate);
    return { start, end: addDays(start, 6) };
  }, [viewMode, anchorDate]);

  const fetchTasks = useCallback(async () => {
    setLoading(true);
    try {
      const { start, end } = getDateRange();

      const params = new URLSearchParams({
        from_date: toDateKey(start),
        to_date: toDateKey(end)
      });

      Object.entries(filters).forEach(([key, value]) => {
        if (key.endsWith('_label')) return; // display-only companions to the *_id filters
        if (typeof value === 'boolean') params.append(key, value ? 'true' : 'false');
        else if (value) params.append(key, value);
      });

      const response = await authFetch(`/api/v1/jobs/calendar?${params}`);
      const data = await response.json();
      setTasks(data.data || []);

      const rotaParams = new URLSearchParams({
        view: 'list',
        start: toDateKey(start),
        days: String(daysBetween(start, end) + 1)
      });
      const rotaResponse = await authFetch(`/api/v1/on-call/rota?${rotaParams}`);
      if (rotaResponse.ok) {
        const rotaData = await rotaResponse.json();
        setOnCallDays(rotaData.days || {});
      }
    } catch (error) {
      console.error('Failed to fetch tasks:', error);
    } finally {
      setLoading(false);
    }
  }, [authFetch, getDateRange, filters]);

  const fetchEngineers = useCallback(async () => {
    try {
      const response = await authFetch('/api/v1/engineers');
      if (!response.ok) return;
      const data = await response.json();
      setEngineers(data.data || []);
    } catch (error) {
      console.error('Failed to fetch engineers:', error);
    }
  }, [authFetch]);

  const fetchFilterOptions = useCallback(async () => {
    try {
      const assetConfigRes = await authFetch('/api/v1/admin/asset-config');
      if (assetConfigRes.ok) {
        const data = await assetConfigRes.json();
        // Payload nests under data: { data: { asset_types } } — reading the
        // top level left this filter silently empty.
        setAssetTypes((data.data?.asset_types || []).filter(type => type.active !== false));
      }
    } catch (error) {
      console.error('Failed to fetch calendar filter options:', error);
    }
  }, [authFetch]);

  useEffect(() => {
    fetchTasks();
    fetchEngineers();
    fetchFilterOptions();
  }, [fetchTasks, fetchEngineers, fetchFilterOptions]);

  useEffect(() => {
    if (!filterStorageReady.current) return;
    const storageKey = `ids-crm-calendar-filters-${user?.userId || user?.email || 'anonymous'}`;
    localStorage.setItem(storageKey, JSON.stringify(filters));
    localStorage.setItem('ids-crm-calendar-filters', JSON.stringify(filters));
  }, [filters, user]);

  useEffect(() => {
    const storageKey = `ids-crm-calendar-filters-${user?.userId || user?.email || 'anonymous'}`;
    // Coming back, the filters are already as this tab left them. The saved
    // set is shared by every tab, so another tab may have changed it since.
    const saved = returning ? null : localStorage.getItem(storageKey);
    if (saved) {
      try {
        setFilters(prev => ({ ...prev, ...sanitizeSavedFilters(JSON.parse(saved)) }));
      } catch {
        // Ignore corrupt saved filters.
      }
    }
    filterStorageReady.current = true;
  }, [user, returning]);

  // Only a date the user moved to is remembered. Left on today, the page
  // follows the clock: a tab reloaded the next morning shows that morning,
  // as every load of the page did before lists were remembered.
  const onToday = toDateKey(anchorDate) === toDateKey(new Date());
  useRememberList('/tasks', {
    view: viewMode,
    date: onToday ? '' : toDateKey(anchorDate),
    filters
  });

  const updateFilter = (name, value) => {
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  const resetFilters = () => {
    setFilters(DEFAULT_CALENDAR_FILTERS);
    setFilterResetKey(key => key + 1);
  };

  // Scroll to current time on load in week view
  useEffect(() => {
    if (viewMode === 'week' && calendarRef.current && !loading) {
      const now = new Date();
      const hour = now.getHours();
      const scrollTo = Math.max(0, (hour - 1) * 48); // 48px per hour slot
      calendarRef.current.scrollTop = scrollTo;
    }
  }, [viewMode, loading]);

  const shiftPeriod = (step) => {
    if (viewMode === 'day') {
      setAnchorDate(addDays(anchorDate, step));
    } else if (viewMode === 'month') {
      setAnchorDate(addMonths(anchorDate, step));
    } else {
      setAnchorDate(addDays(anchorDate, 7 * step));
    }
  };

  const prevPeriod = () => shiftPeriod(-1);

  const nextPeriod = () => shiftPeriod(1);

  const goToToday = () => {
    setAnchorDate(startOfDay(new Date()));
    setViewMode('day');
  };

  const getWeekDays = () => Array.from({ length: 7 }, (_, index) => addDays(weekStart, index));

  // Six Monday-first weeks from the Monday on or before the 1st
  const getMonthDays = () => monthGridDays(anchorDate);

  const isToday = (d) => {
    const today = new Date();
    return d.getDate() === today.getDate() &&
           d.getMonth() === today.getMonth() &&
           d.getFullYear() === today.getFullYear();
  };

  const isCurrentMonth = (d) => {
    return d.getMonth() === monthStart.getMonth();
  };

  const isWeekend = (d) => {
    const day = d.getDay();
    return day === 0 || day === 6;
  };

  // Cells are keyed by their local calendar day and tasks by the day the API
  // names. The old toISOString() key made every BST month cell the day before.
  const getTasksForDay = (date) => {
    const dateKey = toDateKey(date);
    return tasks.filter(t => dateKeyOf(t.scheduled_date) === dateKey);
  };

  const getOnCallForDay = (date) => onCallDays[toDateKey(date)] || [];

  // Names each calendar day for screen readers (and tests) from the date it shows
  const getDayLabel = (date) => date.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });

  const getInitials = (name) => (name || '')
    .split(' ')
    .map(part => part[0])
    .join('')
    .slice(0, 3)
    .toUpperCase();

  const renderOnCallInitials = (date) => {
    const assignments = getOnCallForDay(date).filter(item => item.engineer_name);
    if (assignments.length === 0) return null;
    return (
      <div className="on-call-calendar-initials" title={assignments.map(item => `${item.cover_type_label}: ${item.engineer_name}`).join('\n')}>
        {assignments.map(item => (
          <span key={item.assignment_id}>{getInitials(item.engineer_name)}</span>
        ))}
      </div>
    );
  };

  const getTaskTypeClass = (type) => {
    switch (type) {
      case 'reactive_ticket': return 'type-ticket';
      case 'scheduled_work': return 'type-work';
      case 'scheduled_maintenance': return 'type-maintenance';
      case 'crm_opportunity': return 'type-crm';
      case 'commercial_follow_up': return 'type-crm';
      case 'quarterly_health_check': return 'type-qhc';
      case 'annual_service': return 'type-annual';
      case 'health_check': return 'type-qhc';
      case 'custom_service': return 'type-custom';
      case 'remote_check_in': return 'type-remote';
      case 'upgrade': return 'type-upgrade';
      case 'drop_in': return 'type-dropin';
      default: return 'type-default';
    }
  };

  const getTaskTypeLabel = (type) => {
    switch (type) {
      case 'reactive_ticket': return 'Reactive ticket';
      case 'scheduled_work': return 'Scheduled work';
      case 'scheduled_maintenance': return 'Scheduled maintenance';
      case 'crm_opportunity': return 'CRM / opportunity';
      case 'commercial_follow_up': return 'Commercial follow-up';
      case 'quarterly_health_check': return 'Quarterly health check';
      case 'annual_service': return 'Annual service';
      case 'health_check': return 'Quarterly Insp';
      case 'custom_service': return 'Service';
      case 'remote_check_in': return 'Remote check-in';
      case 'upgrade': return 'Upgrade';
      case 'drop_in': return 'Drop-in';
      case 'sales_action': return 'Sales task';
      default: return type?.replace(/_/g, ' ');
    }
  };

  const getStatusClass = (status) => {
    switch (status) {
      case 'scheduled': return 'status-scheduled';
      case 'confirmed': return 'status-confirmed';
      case 'in_progress': return 'status-progress';
      case 'complete': return 'status-complete';
      case 'completed': return 'status-complete';
      case 'overdue': return 'status-overdue';
      case 'cancelled': return 'status-cancelled';
      default: return '';
    }
  };

  const formatTime = (time) => {
    if (!time) return '';
    return time.substring(0, 5);
  };

  const formatDuration = (mins) => {
    if (!mins) return '';
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    if (h === 0) return `${m} min`;
    if (m === 0) return `${h} hr`;
    return `${h} hr ${m} min`;
  };

  const getTimelineBodyField = (body, label) => {
    const line = String(body || '').split('\n').find(item => item.toLowerCase().startsWith(`${label.toLowerCase()}:`));
    return line ? line.slice(label.length + 1).trim() : '';
  };

  const getCommercialFollowUpContact = (task) => (
    task.contact_name || getTimelineBodyField(task.notes, 'Contact')
  );

  // A job with several robots is labelled by its primary robot plus how many
  // more are on it ("IDS-A-0103 +2"). Rows without asset_count are one robot.
  const getExtraRobotCount = (task) => Math.max(0, (Number(task.asset_count) || 1) - 1);
  const getAssetLabel = (task) => {
    if (!task.asset_ref) return '';
    const extra = getExtraRobotCount(task);
    return extra > 0 ? `${task.asset_ref} +${extra}` : task.asset_ref;
  };

  const getTimeTop = (time, fallbackHour = 9) => {
    if (!time) return fallbackHour * 48;
    const [hours, minutes] = time.split(':').map(Number);
    if (!Number.isFinite(hours) || !Number.isFinite(minutes)) return fallbackHour * 48;
    return (hours * 48) + (minutes * 0.8);
  };

  const getTimeMinutes = (time, fallbackHour = 9) => {
    if (!time) return fallbackHour * 60;
    const [hours, minutes] = time.split(':').map(Number);
    if (!Number.isFinite(hours) || !Number.isFinite(minutes)) return fallbackHour * 60;
    return (hours * 60) + minutes;
  };

  const getTaskVisualRange = (task) => {
    const start = getTimeMinutes(task.scheduled_start_time, 9);
    const duration = Math.max(30, task.estimated_duration_mins || 60);
    return {
      start,
      end: start + duration,
      duration
    };
  };

  const getTaskKey = (task) => `${task.calendar_source || 'task'}-${task.calendar_id || task.task_id}`;

  const getOverlappingTaskLayouts = (dayTasks) => {
    const sorted = dayTasks
      .map(task => ({ task, ...getTaskVisualRange(task) }))
      .sort((a, b) => (
        a.start - b.start ||
        a.end - b.end ||
        String(getTaskKey(a.task)).localeCompare(String(getTaskKey(b.task)))
      ));

    const layoutByKey = {};
    let cluster = [];
    let clusterEnd = -1;

    const flushCluster = () => {
      if (cluster.length === 0) return;

      const laneEnds = [];
      const assigned = cluster.map(item => {
        let lane = laneEnds.findIndex(end => end <= item.start);
        if (lane === -1) {
          lane = laneEnds.length;
          laneEnds.push(item.end);
        } else {
          laneEnds[lane] = item.end;
        }
        return { ...item, lane };
      });
      const laneCount = Math.max(1, laneEnds.length);

      assigned.forEach(item => {
        layoutByKey[getTaskKey(item.task)] = {
          lane: item.lane,
          laneCount
        };
      });
    };

    sorted.forEach(item => {
      if (cluster.length === 0 || item.start < clusterEnd) {
        cluster.push(item);
        clusterEnd = Math.max(clusterEnd, item.end);
        return;
      }

      flushCluster();
      cluster = [item];
      clusterEnd = item.end;
    });

    flushCluster();
    return layoutByKey;
  };

  const openSelectedTask = (task) => {
    setSelectedTask(task);

    if (task.calendar_source === 'scheduled_maintenance') {
      setMaintenanceForm({
        current_due_date: dateKeyOf(task.scheduled_date),
        scheduled_start_time: formatTime(task.scheduled_start_time),
        scheduled_end_time: formatTime(task.scheduled_end_time),
        notes: task.notes || '',
        assigned_engineer_ids: task.assigned_engineer_ids || []
      });
    }
  };

  const weekDays = getWeekDays();
  const monthDays = getMonthDays();

  // Generate 24-hour time slots
  const timeSlots = [];
  for (let h = 0; h < 24; h++) {
    timeSlots.push(`${h.toString().padStart(2, '0')}:00`);
  }

  const dayNames = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

  // Separate maintenance tasks (all-day 7am-6pm) from time-scheduled tasks
  const isAllDayMaintenance = (task) => {
    if (task.calendar_source === 'scheduled_maintenance') {
      return !task.scheduled_start_time;
    }
    if (task.calendar_source === 'crm_opportunity') return true;
    // A Scheduled Work job keeps its own time slot whatever its type, so a
    // Quarterly Health Check or Annual Service job booked from New Work sits
    // at its start time like any other job, not in the maintenance stripe.
    if (task.calendar_source === 'scheduled_work') return false;
    return task.task_type === 'annual_service' ||
           task.task_type === 'health_check' ||
           task.task_type === 'quarterly_health_check';
  };

  const toggleMaintenanceEngineer = (engineerId) => {
    setMaintenanceForm(prev => {
      const ids = prev.assigned_engineer_ids || [];
      return {
        ...prev,
        assigned_engineer_ids: ids.includes(engineerId)
          ? ids.filter(id => id !== engineerId)
          : [...ids, engineerId]
      };
    });
  };

  const saveMaintenanceTask = async () => {
    if (!selectedTask?.maintenance_task_id) return;
    setSavingMaintenance(true);
    try {
      const response = await authFetch(`/api/v1/maintenance/tasks/${selectedTask.maintenance_task_id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          current_due_date: maintenanceForm.current_due_date,
          scheduled_start_time: maintenanceForm.scheduled_start_time || null,
          scheduled_end_time: maintenanceForm.scheduled_end_time || null,
          notes: maintenanceForm.notes || null,
          assigned_engineer_ids: maintenanceForm.assigned_engineer_ids || []
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to update maintenance task');
      }

      await fetchTasks();
      setSelectedTask(null);
    } catch (error) {
      showToast(error.message || 'Failed to update maintenance task', 'error');
    } finally {
      setSavingMaintenance(false);
    }
  };

  const getMaintenanceTasksForDay = (date) => {
    return getTasksForDay(date).filter(isAllDayMaintenance);
  };

  const getScheduledTasksForDay = (date) => {
    return getTasksForDay(date).filter(t => !isAllDayMaintenance(t));
  };

  return (
    <div className="tasks-page">
      {toastElement}
      {/* Top Bar */}
      <div className="tasks-topbar">
        <h1 className="tasks-title">Scheduling</h1>

        <div className="view-toggle">
          <button
            className={viewMode === 'week' ? 'active' : ''}
            onClick={() => setViewMode('week')}
          >
            Week
          </button>
          <button
            className={viewMode === 'month' ? 'active' : ''}
            onClick={() => setViewMode('month')}
          >
            Month
          </button>
          <button
            className={viewMode === 'list' ? 'active' : ''}
            onClick={() => setViewMode('list')}
          >
            List
          </button>
        </div>

        <button className="today-btn" onClick={goToToday}>Today</button>

        <div className="period-nav">
          <button className="nav-btn" onClick={prevPeriod}>&lt;</button>
          <span className="period-label">
            {viewMode === 'day'
              ? selectedDate.toLocaleDateString('default', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })
              : viewMode === 'month'
                ? formatMonthRange(monthStart)
                : formatWeekRange(weekStart)}
          </span>
          <button className="nav-btn" onClick={nextPeriod}>&gt;</button>
        </div>

        {/* Team leaders and up book scheduled work (Gareth 2026-09-23). */}
        {getCapabilities(user).schedule_work && (
          <Link to="/tasks/new" className="add-task-btn">
            + New task
          </Link>
        )}
      </div>

      <div className="calendar-filter-bar">
        <div className="calendar-filter-types">
          {[
            ['crm', 'CRM / opportunities'],
            ['reactive_tickets', 'Reactive tickets'],
            ['scheduled_work', 'Scheduled work'],
            ['scheduled_maintenance', 'Scheduled maintenance']
          ].map(([key, label]) => (
            <label key={key} className={`calendar-filter-chip ${filters[key] ? 'active' : ''}`}>
              <input
                type="checkbox"
                checked={filters[key]}
                onChange={(event) => updateFilter(key, event.target.checked)}
              />
              {label}
            </label>
          ))}
          <label className={`calendar-filter-chip ${filters.my ? 'active' : ''}`}>
            <input
              type="checkbox"
              checked={filters.my}
              onChange={(event) => updateFilter('my', event.target.checked)}
            />
            My Work
          </label>
        </div>
        <div className="calendar-filter-fields">
          <select value={filters.status} onChange={(event) => updateFilter('status', event.target.value)}>
            <option value="">Any status</option>
            {['open', 'in_progress', 'awaiting_customer', 'awaiting_parts', 'escalated', 'closed', 'scheduled', 'confirmed', 'complete', 'completed', 'overdue'].map(status => (
              <option key={status} value={status}>{status.replace(/_/g, ' ')}</option>
            ))}
          </select>
          <select value={filters.assigned_user} onChange={(event) => updateFilter('assigned_user', event.target.value)}>
            <option value="">Any assigned user</option>
            {engineers.map(engineer => (
              <option key={engineer.engineer_id || engineer.user_id} value={engineer.engineer_id || engineer.user_id}>{engineer.full_name}</option>
            ))}
          </select>
          <select value={filters.assigned_group} onChange={(event) => updateFilter('assigned_group', event.target.value)}>
            <option value="">Any assigned group</option>
            <option value="maintenance_planning_owner">Maintenance Planning Owner</option>
          </select>
          <div className="calendar-filter-search">
            <CustomerSearchSelect
              key={`customer-${filterResetKey}`}
              id="calendar_customer_filter"
              kind="customer"
              value={filters.customer_id}
              initialLabel={filters.customer_label}
              placeholder="Any customer"
              clearable
              selectedIndicator={null}
              onSelect={(row) => setFilters(prev => ({
                ...prev,
                customer_id: row ? row.customer_id : '',
                customer_label: row ? row.business_name : ''
              }))}
            />
          </div>
          <div className="calendar-filter-search">
            <CustomerSearchSelect
              key={`distributor-${filterResetKey}`}
              id="calendar_distributor_filter"
              kind="distributor"
              value={filters.distributor_id}
              initialLabel={filters.distributor_label}
              placeholder="Any distributor"
              clearable
              selectedIndicator={null}
              onSelect={(row) => setFilters(prev => ({
                ...prev,
                distributor_id: row ? row.customer_id : '',
                distributor_label: row ? row.business_name : ''
              }))}
            />
          </div>
          <select value={filters.asset_type} onChange={(event) => updateFilter('asset_type', event.target.value)}>
            <option value="">Any product type</option>
            {assetTypes.map(type => (
              <option key={type.value} value={type.value}>{type.label}</option>
            ))}
          </select>
          <select value={filters.priority} onChange={(event) => updateFilter('priority', event.target.value)}>
            <option value="">Any priority</option>
            {['critical', 'high', 'normal', 'low'].map(priority => (
              <option key={priority} value={priority}>{priority}</option>
            ))}
          </select>
          <button type="button" onClick={resetFilters}>Reset</button>
        </div>
      </div>

      <div className="tasks-body">
        {/* Main Calendar Grid */}
        <div className="calendar-area" ref={calendarRef}>
          {loading ? (
            <div className="loading-calendar">Loading schedule...</div>
          ) : viewMode === 'day' ? (
            <div className="day-grid">
              {/* Time column */}
              <div className="time-column">
                <div className="time-header"></div>
                {timeSlots.map(time => (
                  <div key={time} className="time-slot">{time}</div>
                ))}
              </div>

              {/* Day content area - full width */}
              <div className="day-content" role="group" aria-label={getDayLabel(selectedDate)}>
                <div className={`day-header ${isToday(selectedDate) ? 'today' : ''}`}>
                  <span className="day-name">
                    {selectedDate.toLocaleString('default', { weekday: 'long' })}
                  </span>
                  <span className="day-num">{selectedDate.getDate()}</span>
                  {renderOnCallInitials(selectedDate)}
                </div>
                <div className="day-tasks-area">
                  {/* Current time indicator */}
                  {isToday(selectedDate) && (
                    <div
                      className="current-time-indicator"
                      style={{ top: `${(new Date().getHours() * 48) + (new Date().getMinutes() * 0.8)}px` }}
                    />
                  )}

                  {/* All tasks for the day spread horizontally */}
                  {(() => {
                    const dayTasks = getTasksForDay(selectedDate);
                    const taskCount = dayTasks.length;
                    if (taskCount === 0) {
                      return <div className="no-tasks-day">No tasks scheduled for today</div>;
                    }

                    return dayTasks.map((task, idx) => {
                      const top = getTimeTop(task.scheduled_start_time, isAllDayMaintenance(task) ? 7 : 9);
                      const durationMins = task.estimated_duration_mins || 60;
                      const blockHeight = durationMins * 0.8;
                      const taskWidth = 100 / taskCount;

                      return (
                        <div
                          key={task.task_id}
                          className={`task-block day-task-block ${getTaskTypeClass(task.task_type)} ${selectedTask?.task_id === task.task_id ? 'selected' : ''} ${task.status === 'overdue' ? 'overdue' : ''}`}
                          onClick={() => openSelectedTask(task)}
                          style={{
                            position: 'absolute',
                            top: `${top}px`,
                            height: `${Math.max(48, blockHeight)}px`,
                            left: `${idx * taskWidth}%`,
                            width: `${taskWidth}%`,
                            padding: '8px 12px'
                          }}
                        >
                          <div className="task-title">{getTaskTypeLabel(task.task_type)}</div>
                          <div className="task-sub">{task.customer_name}</div>
                          {task.asset_ref && <div className="task-sub">{getAssetLabel(task)}</div>}
                          {task.calendar_source === 'commercial_follow_up' && getCommercialFollowUpContact(task) && (
                            <div className="task-sub">{getCommercialFollowUpContact(task)}</div>
                          )}
                          {task.assigned_to_name && (
                            <div className="task-sub">{task.assigned_to_name}</div>
                          )}
                          {task.scheduled_start_time && (
                            <div className="task-time">{task.scheduled_start_time.substring(0, 5)}</div>
                          )}
                        </div>
                      );
                    });
                  })()}
                </div>
              </div>
            </div>
          ) : viewMode === 'week' ? (
            <div className="week-grid week-grid-7day">
              {/* Time column */}
              <div className="time-column">
                <div className="time-header"></div>
                {timeSlots.map(time => (
                  <div key={time} className="time-slot">{time}</div>
                ))}
              </div>

              {/* Day columns - 7 days with split layout */}
              {weekDays.map(day => {
                const maintenanceTasks = getMaintenanceTasksForDay(day);
                const scheduledTasks = getScheduledTasksForDay(day);
                const hasMaintenanceTasks = maintenanceTasks.length > 0;
                const scheduledTaskLayouts = getOverlappingTaskLayouts(scheduledTasks);

                return (
                  <div
                    key={toDateKey(day)}
                    className={`day-column ${isWeekend(day) ? 'weekend' : ''} ${hasMaintenanceTasks ? 'has-maintenance' : ''}`}
                    role="group"
                    aria-label={getDayLabel(day)}
                  >
                    <div className={`day-header ${isToday(day) ? 'today' : ''}`}>
                      <span className="day-name">
                        {day.toLocaleString('default', { weekday: 'short' })}
                      </span>
                      <span className="day-num">{day.getDate()}</span>
                      {renderOnCallInitials(day)}
                    </div>
                    <div className="day-content-split">
                      {/* Left stripe: All-day maintenance tasks (7am-6pm block) */}
                      {hasMaintenanceTasks && (
                        <div className="maintenance-stripe">
                          {maintenanceTasks.map((task, idx) => {
                            const taskCount = maintenanceTasks.length;
                            const stripeWidth = 50;
                            const taskWidth = stripeWidth / taskCount;
                            const durationHours = task.duration_hours || ((task.estimated_duration_mins || (task.task_type === 'annual_service' ? 120 : 60)) / 60);
                            const blockHeight = durationHours * 48;

                            return (
                              <div
                                key={task.task_id}
                                className={`maintenance-task-block ${getTaskTypeClass(task.task_type)} ${selectedTask?.task_id === task.task_id ? 'selected' : ''} ${task.status === 'overdue' ? 'overdue' : ''}`}
                                onClick={() => openSelectedTask(task)}
                                title={`${getTaskTypeLabel(task.task_type)} - ${task.customer_name} - ${getAssetLabel(task)} (${durationHours}h)`}
                                style={{
                                  position: 'absolute',
                                  top: `${7 * 48}px`,
                                  height: `${blockHeight}px`,
                                  left: `${idx * taskWidth}px`,
                                  width: `${taskWidth}px`
                                }}
                              >
                                <div className="maint-type">{task.task_type === 'annual_service' ? 'AS' : `QI${task.health_check_number || ''}`}</div>
                                <div className="maint-customer">{task.customer_name?.split(' ')[0]}</div>
                                {task.asset_ref && <div className="maint-asset">{getAssetLabel(task)}</div>}
                              </div>
                            );
                          })}
                        </div>
                      )}

                      {/* Right area: Time-scheduled tasks */}
                      <div className="scheduled-slots">
                        {/* Current time indicator */}
                        {isToday(day) && (
                          <div
                            className="current-time-indicator"
                            style={{ top: `${(new Date().getHours() * 48) + (new Date().getMinutes() * 0.8)}px` }}
                          />
                        )}
                        {scheduledTasks.map(task => {
                          const top = getTimeTop(task.scheduled_start_time, 9);
                          const durationMins = task.estimated_duration_mins || 60;
                          const blockHeight = durationMins * 0.8;
                          const layout = scheduledTaskLayouts[getTaskKey(task)] || { lane: 0, laneCount: 1 };
                          const widthPercent = 100 / layout.laneCount;
                          const leftPercent = layout.lane * widthPercent;

                          return (
                            <div
                              key={task.task_id}
                              className={`task-block week-task-block ${getTaskTypeClass(task.task_type)} ${selectedTask?.task_id === task.task_id ? 'selected' : ''} ${task.status === 'overdue' ? 'overdue' : ''}`}
                              onClick={() => openSelectedTask(task)}
                              style={{
                                position: 'absolute',
                                top: `${top}px`,
                                height: `${Math.max(24, blockHeight)}px`,
                                left: `calc(${leftPercent}% + 2px)`,
                                width: `calc(${widthPercent}% - 4px)`
                              }}
                            >
                              <div className="task-title">{getTaskTypeLabel(task.task_type)}</div>
                              <div className="task-sub">{task.customer_name}</div>
                              {task.calendar_source === 'commercial_follow_up' && getCommercialFollowUpContact(task) && (
                                <div className="task-sub">{getCommercialFollowUpContact(task)}</div>
                              )}
                              {task.assigned_to_name && (
                                <div className="task-sub">{task.assigned_to_name}</div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : viewMode === 'month' ? (
            <div className="month-grid">
              {/* Day name headers */}
              <div className="month-header-row">
                {dayNames.map(name => (
                  <div key={name} className="month-day-header">{name}</div>
                ))}
              </div>
              {/* Calendar days */}
              <div className="month-days-grid">
                {monthDays.map((day, index) => (
                  <div
                    key={index}
                    className={`month-day-cell ${isToday(day) ? 'today' : ''} ${!isCurrentMonth(day) ? 'other-month' : ''} ${isWeekend(day) ? 'weekend' : ''}`}
                    role="group"
                    aria-label={getDayLabel(day)}
                  >
                    <div className="month-day-num">
                      <span>{day.getDate()}</span>
                      {renderOnCallInitials(day)}
                    </div>
                    <div className="month-day-tasks">
                      {getTasksForDay(day).slice(0, 3).map(task => (
                        <div
                          key={task.task_id}
                          className={`month-task-chip ${getTaskTypeClass(task.task_type)} ${task.status === 'overdue' ? 'overdue' : ''}`}
                          onClick={() => openSelectedTask(task)}
                          title={`${getTaskTypeLabel(task.task_type)} - ${task.customer_name}${getCommercialFollowUpContact(task) ? ` - ${getCommercialFollowUpContact(task)}` : ''}${task.assigned_to_name ? ` - ${task.assigned_to_name}` : ''}`}
                        >
                          {getTaskTypeLabel(task.task_type).substring(0, 12)}
                        </div>
                      ))}
                      {getTasksForDay(day).length > 3 && (
                        <div className="month-more-tasks">
                          +{getTasksForDay(day).length - 3} more
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="list-view">
              {tasks.length === 0 ? (
                <div className="empty-list">No tasks scheduled for this period</div>
              ) : (
                <table className="tasks-table">
                  <thead>
                    <tr>
                      <th>Ref</th>
                      <th>Type</th>
                      <th>Customer / Asset</th>
                      <th>Date</th>
                      <th>Time</th>
                      <th>Duration</th>
                      <th>Assigned</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {tasks.map(task => (
                      <tr
                        key={task.task_id}
                        onClick={() => openSelectedTask(task)}
                        className="task-row"
                      >
                        <td className="ref-cell">{task.task_ref}</td>
                        <td>
                          <span className={`type-chip ${getTaskTypeClass(task.task_type)}`}>
                            {getTaskTypeLabel(task.task_type)}
                          </span>
                        </td>
                        <td>
                          <div className="customer-cell">{task.customer_name}</div>
                          {task.asset_ref && (
                            <div className="asset-cell">{getAssetLabel(task)}</div>
                          )}
                          {task.calendar_source === 'commercial_follow_up' && getCommercialFollowUpContact(task) && (
                            <div className="asset-cell">{getCommercialFollowUpContact(task)}</div>
                          )}
                        </td>
                        <td>{formatDateKey(task.scheduled_date)}</td>
                        <td>{formatTime(task.scheduled_start_time) || 'All day'}</td>
                        <td>{formatDuration(task.estimated_duration_mins)}</td>
                        <td>{task.assigned_to_name || '-'}</td>
                        <td>
                          <span className={`status-chip ${getStatusClass(task.status)}`}>
                            {task.status}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}
        </div>

        {/* Right Panel - Task Detail */}
        <div className="detail-panel">
          {selectedTask ? (
            <>
              <div className="panel-header">
                <div className="panel-title">{getTaskTypeLabel(selectedTask.task_type)}</div>
                <div className="panel-sub">
                  {selectedTask.task_ref} · {formatDateKey(selectedTask.scheduled_date)}
                  {selectedTask.scheduled_start_time && `, ${formatTime(selectedTask.scheduled_start_time)}`}
                </div>
              </div>
              <div className="panel-body">
                <div className="panel-section">
                  <div className="section-label">
                    {selectedTask.calendar_source === 'commercial_follow_up' ? 'Customer' : 'Farm & asset'}
                  </div>
                  <div className="section-value">{selectedTask.customer_name}</div>
                  {selectedTask.calendar_source !== 'commercial_follow_up' && selectedTask.asset_ref && (
                    getExtraRobotCount(selectedTask) > 0 && selectedTask.asset_refs ? (
                      <div className="section-sub">{selectedTask.asset_count} robots · {selectedTask.asset_refs}</div>
                    ) : (
                      <div className="section-sub">{selectedTask.robot_type} · {selectedTask.asset_ref}</div>
                    )
                  )}
                </div>

                {selectedTask.site_name && (
                  <div className="panel-section">
                    <div className="section-label">Site</div>
                    <div className="section-value">{selectedTask.site_name}</div>
                  </div>
                )}

                {selectedTask.distributor_name && (
                  <div className="panel-section">
                    <div className="section-label">Distributor</div>
                    <div className="section-value">{selectedTask.distributor_name}</div>
                  </div>
                )}

                {selectedTask.title && (
                  <div className="panel-section">
                    <div className="section-label">
                      {selectedTask.calendar_source === 'reactive_ticket'
                        ? 'Reported issue'
                        : selectedTask.calendar_source === 'scheduled_work'
                          ? 'Work title'
                          : selectedTask.calendar_source === 'crm_opportunity'
                            ? 'Opportunity'
                            : 'Summary'}
                    </div>
                    <div className="section-value">{selectedTask.title}</div>
                    {selectedTask.priority && (
                      <div className="section-sub">Priority: {selectedTask.priority}</div>
                    )}
                  </div>
                )}

                {selectedTask.assigned_to_name && (() => {
                  // Several engineers on one job (Phase 5): the API sends a
                  // scheduled job's names comma-joined, lead first, with
                  // engineer_ids in the same order - one avatar each. Any
                  // other row keeps its single name as it always did.
                  const names = selectedTask.calendar_source === 'scheduled_work'
                    ? selectedTask.assigned_to_name.split(',').map(name => name.trim()).filter(Boolean)
                    : [selectedTask.assigned_to_name];
                  const ids = Array.isArray(selectedTask.engineer_ids) ? selectedTask.engineer_ids : [];
                  const several = names.length > 1;
                  return (
                    <div className="panel-section">
                      <div className="section-label">{several ? 'Engineers' : 'Assigned to'}</div>
                      <div className="assigned-list" style={several ? { display: 'grid', gap: '8px' } : undefined}>
                        {names.map((name, index) => (
                          <div className="assigned-row" key={ids.length === names.length ? ids[index] : `${index}-${name}`}>
                            <div className="assigned-avatar">
                              {name.split(' ').map(n => n[0]).join('')}
                            </div>
                            <div>
                              <div className="section-value">{name}</div>
                              <div className="section-sub">
                                {selectedTask.calendar_source === 'commercial_follow_up'
                                  ? 'Assigned user'
                                  : (several && index === 0 ? 'Lead engineer' : 'Engineer')}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })()}

                <div className="panel-section">
                  <div className="section-label">Type &amp; status</div>
                  <div className="chip-row">
                    <span className={`type-chip ${getTaskTypeClass(selectedTask.task_type)}`}>
                      {getTaskTypeLabel(selectedTask.task_type)}
                    </span>
                    <span className={`status-chip ${getStatusClass(selectedTask.status)}`}>
                      {selectedTask.status}
                    </span>
                  </div>
                </div>

                <div className="panel-section">
                  <div className="section-label">Schedule</div>
                  <div className="section-value">
                    {formatTime(selectedTask.scheduled_start_time) || 'All day'}{selectedTask.scheduled_end_time ? ` - ${formatTime(selectedTask.scheduled_end_time)}` : ''}
                  </div>
                  <div className="section-sub">Est. {formatDuration(selectedTask.estimated_duration_mins)}</div>
                </div>

                {selectedTask.calendar_source === 'commercial_follow_up' && (
                  <>
                    {(selectedTask.contact_name || getTimelineBodyField(selectedTask.notes, 'Contact')) && (
                      <div className="panel-section">
                        <div className="section-label">Contact</div>
                        <div className="section-value">
                          {selectedTask.contact_name || getTimelineBodyField(selectedTask.notes, 'Contact')}
                        </div>
                      </div>
                    )}

                    {getTimelineBodyField(selectedTask.notes, 'Communication method') && (
                      <div className="panel-section">
                        <div className="section-label">Communication method</div>
                        <div className="section-value">{getTimelineBodyField(selectedTask.notes, 'Communication method')}</div>
                      </div>
                    )}

                    <div className="panel-section">
                      <div className="section-label">Activity / purpose</div>
                      <div className="section-value">{selectedTask.next_action || selectedTask.title}</div>
                      {selectedTask.entry_type && (
                        <div className="section-sub">{selectedTask.entry_type.replace(/_/g, ' ')}</div>
                      )}
                    </div>

                    {selectedTask.notes && (
                      <div className="panel-section">
                        <div className="section-label">Notes</div>
                        <div className="section-value follow-up-notes">{selectedTask.notes}</div>
                      </div>
                    )}
                  </>
                )}

                <div className="panel-actions">
                  {selectedTask.calendar_source === 'commercial_follow_up' ? (
                    <>
                      <Link to={`/customers/${selectedTask.customer_id}?tab=timeline&complete_activity=${selectedTask.task_id}`} className="action-btn primary">
                        Open completion form
                      </Link>
                      <Link to={`/customers/${selectedTask.customer_id}?tab=timeline`} className="action-btn">
                        View customer timeline
                      </Link>
                    </>
                  ) : selectedTask.calendar_source === 'reactive_ticket' ? (
                    <>
                      <Link to={`/tickets/${selectedTask.task_id}`} className="action-btn primary">
                        Open ticket
                      </Link>
                      {selectedTask.customer_id && (
                        <Link to={`/customers/${selectedTask.customer_id}?tab=timeline`} className="action-btn">
                          View customer timeline
                        </Link>
                      )}
                    </>
                  ) : selectedTask.calendar_source === 'scheduled_work' ? (
                    <>
                      <Link to={`/tasks/${selectedTask.task_id}`} className="action-btn primary">
                        Open scheduled work
                      </Link>
                      {selectedTask.customer_id && (
                        <Link to={`/customers/${selectedTask.customer_id}?tab=timeline`} className="action-btn">
                          View customer timeline
                        </Link>
                      )}
                    </>
                  ) : selectedTask.calendar_source === 'crm_opportunity' ? (
                    <>
                      <Link to={`/pipeline/${selectedTask.task_id}`} className="action-btn primary">
                        Open opportunity
                      </Link>
                      {selectedTask.customer_id && (
                        <Link to={`/customers/${selectedTask.customer_id}?tab=timeline`} className="action-btn">
                          View customer timeline
                        </Link>
                      )}
                    </>
                  ) : selectedTask.calendar_source === 'scheduled_maintenance' ? (
                    <>
                      {selectedTask.maintenance_task_id && (
                        <Link to={`/maintenance/${selectedTask.maintenance_task_id}`} className="action-btn primary">
                          Open maintenance form
                        </Link>
                      )}
                      {selectedTask.asset_id && (
                        <Link to={`/assets/${selectedTask.asset_id}`} className="action-btn">
                          View asset
                        </Link>
                      )}
                      {selectedTask.status !== 'completed' && (
                        <>
                          <div className="maintenance-edit-form">
                            <div className="section-label">Edit scheduled task</div>
                            <label className="maintenance-field">
                              <span>Date</span>
                              <input
                                type="date"
                                value={maintenanceForm.current_due_date}
                                onChange={(e) => setMaintenanceForm(prev => ({ ...prev, current_due_date: e.target.value }))}
                              />
                            </label>
                            <div className="maintenance-time-row">
                              <label className="maintenance-field">
                                <span>Start</span>
                                <input
                                  type="time"
                                  value={maintenanceForm.scheduled_start_time}
                                  onChange={(e) => setMaintenanceForm(prev => ({ ...prev, scheduled_start_time: e.target.value }))}
                                />
                              </label>
                              <label className="maintenance-field">
                                <span>Finish</span>
                                <input
                                  type="time"
                                  value={maintenanceForm.scheduled_end_time}
                                  onChange={(e) => setMaintenanceForm(prev => ({ ...prev, scheduled_end_time: e.target.value }))}
                                />
                              </label>
                            </div>
                            <div className="maintenance-help">
                              Leave both times blank to use the asset configuration default duration.
                            </div>
                            <label className="maintenance-field">
                              <span>Task notes</span>
                              <textarea
                                value={maintenanceForm.notes}
                                onChange={(e) => setMaintenanceForm(prev => ({ ...prev, notes: e.target.value }))}
                                rows={3}
                                placeholder="Notes for the scheduled visit"
                              />
                            </label>
                            <div className="maintenance-field">
                              <span>Engineers</span>
                              <div className="engineer-check-list">
                                {engineers.length === 0 ? (
                                  <div className="maintenance-help">No engineers are currently configured.</div>
                                ) : engineers.map(engineer => (
                                  <label key={engineer.engineer_id} className="engineer-check">
                                    <input
                                      type="checkbox"
                                      checked={(maintenanceForm.assigned_engineer_ids || []).includes(engineer.engineer_id)}
                                      onChange={() => toggleMaintenanceEngineer(engineer.engineer_id)}
                                    />
                                    <span>{engineer.full_name}</span>
                                  </label>
                                ))}
                              </div>
                            </div>
                            <button
                              className="action-btn primary"
                              onClick={saveMaintenanceTask}
                              disabled={savingMaintenance}
                            >
                              {savingMaintenance ? 'Saving...' : 'Save task changes'}
                            </button>
                          </div>
                        </>
                      )}
                    </>
                  ) : (
                    <>
                      <Link to={`/tasks/${selectedTask.task_id}`} className="action-btn primary">
                        View details
                      </Link>
                      <button className="action-btn">Reschedule</button>
                    </>
                  )}
                </div>
              </div>
            </>
          ) : (
            <div className="panel-empty">
              <p>Select a task to view details</p>
            </div>
          )}
        </div>
      </div>

    </div>
  );
}

export default Tasks;
